import React, { useState, useEffect } from 'react';
import { Search, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function ConsolidadoNotas({ filters }) {
    const [estudiantes, setEstudiantes] = useState([]);
    const [notasProceso, setNotasProceso] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (filters.id_asignatura) {
            loadEstudiantes();
            loadTodasLasNotasProceso();
        }
    }, [filters.id_asignatura, filters.id_paralelo, filters.gestion]);

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo })
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading estudiantes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTodasLasNotasProceso = async () => {
        try {
            // Cargar notas de todos los 4 periodos
            const promises = [1, 2, 3, 4].map(per =>
                fetch(`${API_URL}/docente/notas-proceso?${new URLSearchParams({
                    id_asignatura: filters.id_asignatura,
                    ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                    gestion: filters.gestion,
                    periodo: per
                })}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                })
            );

            const responses = await Promise.all(promises);
            const dataArray = await Promise.all(responses.map(r => r.json()));

            // Organizar por estudiante y periodo
            const notasMap = {};
            dataArray.forEach((data, periodoIdx) => {
                const periodo = periodoIdx + 1;
                (data.notas || []).forEach(n => {
                    if (!notasMap[n.id_estudiante]) {
                        notasMap[n.id_estudiante] = {};
                    }
                    if (!notasMap[n.id_estudiante][periodo]) {
                        notasMap[n.id_estudiante][periodo] = [];
                    }
                    notasMap[n.id_estudiante][periodo].push(n.nota);
                });
            });
            setNotasProceso(notasMap);
        } catch (error) {
            console.error('Error loading notas proceso:', error);
        }
    };

    const calcularPeriodo = (id_estudiante, periodo) => {
        const notasEst = notasProceso[id_estudiante] || {};
        const notasPer = notasEst[periodo] || [];

        if (notasPer.length === 0) return '-';
        const promedio = notasPer.reduce((sum, n) => sum + n, 0) / notasPer.length;
        return Math.round(promedio);
    };

    const calcularPromedioFinal = (id_estudiante) => {
        const periodos = [1, 2, 3, 4];
        const promediosPeriodo = periodos
            .map(p => parseFloat(calcularPeriodo(id_estudiante, p)))
            .filter(p => !isNaN(p));

        if (promediosPeriodo.length === 0) return '-';
        const promedio = promediosPeriodo.reduce((sum, p) => sum + p, 0) / promediosPeriodo.length;
        return Math.round(promedio);
    };

    const filteredEstudiantes = estudiantes.filter(est =>
        searchTerm === '' ||
        est.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        est.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-4">
            {/* Barra de herramientas */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">Notas Consolidadas</h3>
                    <p className="text-sm text-gray-600">Gestión {filters.gestion}</p>
                    <p className="text-xs text-gray-500">Promedio de los 4 periodos</p>
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar estudiante..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>

                    <button
                        onClick={() => alert('Descarga funcionalidad')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        <Download size={18} />
                        Descargar
                    </button>
                </div>
            </div>

            {/* Tabla */}
            {loading ? (
                <div className="p-8 text-center text-gray-500">Cargando...</div>
            ) : filteredEstudiantes.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    {filters.id_asignatura ? 'No hay estudiantes' : 'Selecciona una asignatura'}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b">
                                <th className="p-3 text-left text-sm font-semibold text-gray-700">No</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-700">Email</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-blue-50">Periodo 1</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-green-50">Periodo 2</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-yellow-50">Periodo 3</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-purple-50">Periodo 4</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-red-50">Promedio Final</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEstudiantes.map((est, idx) => {
                                const p1 = calcularPeriodo(est.id_estudiante, 1);
                                const p2 = calcularPeriodo(est.id_estudiante, 2);
                                const p3 = calcularPeriodo(est.id_estudiante, 3);
                                const p4 = calcularPeriodo(est.id_estudiante, 4);
                                const promedio = calcularPromedioFinal(est.id_estudiante);

                                return (
                                    <tr key={est.id_estudiante} className="border-b hover:bg-gray-50">
                                        <td className="p-3 text-sm">{idx + 1}</td>
                                        <td className="p-3 text-sm font-medium">{est.codigo}</td>
                                        <td className="p-3 text-sm">{est.email}</td>
                                        <td className="p-3 text-center bg-blue-50">
                                            <span className="font-semibold text-blue-700">{p1}</span>
                                        </td>
                                        <td className="p-3 text-center bg-green-50">
                                            <span className="font-semibold text-green-700">{p2}</span>
                                        </td>
                                        <td className="p-3 text-center bg-yellow-50">
                                            <span className="font-semibold text-yellow-700">{p3}</span>
                                        </td>
                                        <td className="p-3 text-center bg-purple-50">
                                            <span className="font-semibold text-purple-700">{p4}</span>
                                        </td>
                                        <td className="p-3 text-center bg-red-50">
                                            <span className="text-lg font-bold text-red-700">{promedio}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
